/**
 * REST API klient pro Tracer-AN nabíječ. Tenké axios wrappery.
 */

import { deleteAsync, getAsync, patchAsync, postAsync } from "./axios";
import type {
  TracerBatteryType,
  TracerDiscoveredSerial,
  TracerListItem,
  TracerProbeResult,
  TracerRecord,
  TracerRuntime,
} from "@client/types/tracer";
import type { WriteBatchResult, WriteFieldResult } from "@client/types/verify";

export const getTracerList = () => getAsync<ApiResp<TracerListItem[]>>("/api/tracer");

export const getTracerSnapshot = (slug: string) =>
  getAsync<ApiResp<{ record: TracerRecord; runtime: TracerRuntime | null }>>(`/api/tracer/${slug}/snapshot`);

export const getTracerSerialPorts = () => getAsync<ApiResp<TracerDiscoveredSerial[]>>("/api/tracer/serial-ports");

export interface TracerDebugFrame {
  length: number;
  hex: string;
  timestamp: number;
}

export const getTracerDebugFrame = (slug: string) => getAsync<ApiResp<TracerDebugFrame>>(`/api/tracer/${slug}/debug`);

export interface ProbePayload {
  transportTarget: string;
  slaveId?: number;
  baudRate?: number;
  timeoutMs?: number;
}

export const postTracerProbe = (data: ProbePayload) => postAsync<ApiResp<TracerProbeResult>>("/api/tracer/probe", data);

export interface CreateTracerPayload {
  slug: string;
  name: string;
  transportTarget: string;
  slaveId?: number;
  baudRate?: number;
  batteryNominalV?: number;
  batteryType?: TracerBatteryType;
  batteryCapacityAh?: number | null;
  notes?: string | null;
  enabled?: boolean;
  order?: number | null;
}

export const postTracer = (data: CreateTracerPayload) => postAsync<ApiResp<TracerRecord>>("/api/tracer", data);

export interface UpdateTracerPayload {
  name?: string;
  transportTarget?: string;
  slaveId?: number;
  baudRate?: number;
  batteryNominalV?: number;
  batteryType?: TracerBatteryType;
  batteryCapacityAh?: number | null;
  notes?: string | null;
  enabled?: boolean;
  order?: number | null;
}

export const patchTracer = (slug: string, data: UpdateTracerPayload) =>
  patchAsync<ApiResp<TracerRecord>>(`/api/tracer/${slug}`, data);

export const deleteTracerReq = (slug: string) => deleteAsync<ApiResp<TracerRecord>>(`/api/tracer/${slug}`);

export const postTracerReconnect = (slug: string) => postAsync<ApiResp<boolean>>(`/api/tracer/${slug}/reconnect`);

// Coil control endpointy — vrací ověřený per-field výsledek (read-back coilu)
export const postTracerLoad = (slug: string, on: boolean) =>
  postAsync<ApiResp<WriteFieldResult>>(`/api/tracer/${slug}/control/load`, { on });

export const postTracerLoadTest = (slug: string, on: boolean) =>
  postAsync<ApiResp<WriteFieldResult>>(`/api/tracer/${slug}/control/load-test`, { on });

export const postTracerLoadForce = (slug: string, on: boolean) =>
  postAsync<ApiResp<WriteFieldResult>>(`/api/tracer/${slug}/control/load-force`, { on });

// Settings batch PATCH
export interface TracerSettingsPatch {
  batteryType?: TracerBatteryType;
  batteryCapacityAh?: number;
  tempCompCoef?: number;
  highVoltageDisconnect?: number;
  chargingLimitVoltage?: number;
  overVoltageReconnect?: number;
  equalizationVoltage?: number;
  boostVoltage?: number;
  floatVoltage?: number;
  boostReconnectVoltage?: number;
  lowVoltageReconnect?: number;
  underVoltageRecover?: number;
  underVoltageWarning?: number;
  lowVoltageDisconnect?: number;
  dischargingLimitVoltage?: number;
  eqChargingCycleDays?: number;
  batTempWarnHigh?: number;
  batTempWarnLow?: number;
  eqDurationMin?: number;
  boostDurationMin?: number;
}

export const patchTracerSettings = (slug: string, data: TracerSettingsPatch) =>
  patchAsync<ApiResp<WriteBatchResult>>(`/api/tracer/${slug}/settings`, data);
